import React, {useEffect, useMemo, useState} from 'react';
import {Button, Modal, Progress, Result, Spin} from 'antd';
import {LogoutOutlined, ReloadOutlined, UnorderedListOutlined, ProfileOutlined} from '@ant-design/icons';
import {useQuery} from '@apollo/client';
import {useSearchParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {GET_MOBILE_COMPLAINT_TASKS, MobileComplaintTasksResponse} from 'graphql/complaintTask/queries';
import {ComplaintTaskRow} from 'graphql/complaintTask/types';
import {logout} from 'utils/auth';
import MobileTaskCard from './components/MobileTaskCard';
import MobileTaskList from './components/MobileTaskList';
import {focusOrder, skipTask} from './mobileQueue';
import {MOBILE_LOGIN_PATH, PARAM_ACTION, PARAM_TASK_ID} from './mobilePaths';

/** 队列自动刷新间隔（拿到新派单 / 他人已处理） */
const POLL_MS = 60 * 1000;
/** 倒计时刷新间隔 */
const TICK_MS = 30 * 1000;

/** 以服务端时间为准走表（避免手机时钟偏差），每 TICK_MS 刷新一次。 */
const useServerNowMs = (serverNow?: string | null): number => {
  const [offset, setOffset] = useState(0);
  const [, setTick] = useState(0);
  useEffect(() => {
    if (serverNow) setOffset(Date.parse(serverNow) - Date.now());
  }, [serverNow]);
  useEffect(() => {
    const id = setInterval(() => setTick((x) => x + 1), TICK_MS);
    return () => clearInterval(id);
  }, []);
  return Date.now() + offset;
};

/**
 * 客诉手机版作业页（/complaint-platform/work-mobile，PRD「客诉任务作业（手机版）」）。
 * 数据 = 登录人可见范围内的待处理任务（同 PC 管理端口径，无查看视角，10-14 确认）；
 * 逐条模式按紧急度一次看一条，正在看的那条钉住（含开始处理后），完成 / 跳过后再取最紧急的；全部列表可挑单处理。
 * 排队、钉住、跳过只影响本页浏览顺序，不改任务本身。
 */
const MobileWorkPage: React.FC = () => {
  const {t} = useTranslation();
  const [params, setParams] = useSearchParams();
  const linkTaskId = Number(params.get(PARAM_TASK_ID)) || undefined;
  const linkAction = params.get(PARAM_ACTION);

  const [mode, setMode] = useState<'focus' | 'list'>('focus');
  const [pinnedId, setPinnedId] = useState<number | undefined>(linkTaskId);
  const [skipped, setSkipped] = useState<number[]>([]);
  const [cleared, setCleared] = useState(0);
  // 本次已完成的任务：队列刷新回来之前先剔掉，避免刚关闭的那条闪回
  const [resolvedIds, setResolvedIds] = useState<number[]>([]);

  const queryOptions = {pollInterval: POLL_MS, fetchPolicy: 'cache-and-network' as const};
  const unresponded = useQuery<MobileComplaintTasksResponse>(GET_MOBILE_COMPLAINT_TASKS,
    {...queryOptions, variables: {stage: 'UNRESPONDED'}});
  const inProgress = useQuery<MobileComplaintTasksResponse>(GET_MOBILE_COMPLAINT_TASKS,
    {...queryOptions, variables: {stage: 'IN_PROGRESS'}});

  const pending: ComplaintTaskRow[] = useMemo(() => {
    const done = new Set(resolvedIds);
    // 两个阶段各自刷新：刚开始处理的任务可能短暂同时出现在两边，按 taskId 去重，以已响应的那条为准。
    // taskId 是 Long，GraphQL 返回字符串：统一转成数字，才能和卡片链接里的 ?taskId= 对上
    const byId = new Map<number, ComplaintTaskRow>();
    [...(unresponded.data?.mobileComplaintTasks.list ?? []), ...(inProgress.data?.mobileComplaintTasks.list ?? [])]
      .map((r) => ({...r, taskId: Number(r.taskId)}))
      .filter((r) => !done.has(r.taskId))
      .forEach((r) => {
        const prev = byId.get(r.taskId);
        if (!prev || (!prev.actualStartAt && r.actualStartAt)) byId.set(r.taskId, r);
      });
    return Array.from(byId.values());
  }, [unresponded.data, inProgress.data, resolvedIds]);
  const nowMs = useServerNowMs(unresponded.data?.mobileComplaintTasks.serverNow
    ?? inProgress.data?.mobileComplaintTasks.serverNow);

  const order = useMemo(() => focusOrder(pending, skipped), [pending, skipped]);
  const currentId = pinnedId ?? order[0]?.taskId;
  const inQueue = currentId !== undefined && pending.some((r) => r.taskId === currentId);
  const position = order.findIndex((r) => r.taskId === currentId);
  const total = cleared + order.length;

  // 刷新失败由 errorLink 统一提示，这里只吞掉 rejection
  const refresh = () => Promise.all([unresponded.refetch(), inProgress.refetch()]).catch(() => undefined);

  // 逐条模式下正在看的那条始终钉住：新来更紧急的任务不会把它顶掉（填了一半的说明 / 附件不丢），
  // 完成、跳过、返回后再取当前最紧急的一条
  useEffect(() => {
    if (mode === 'focus' && pinnedId === undefined && order[0]) setPinnedId(order[0].taskId);
  }, [mode, pinnedId, order]);

  const clearLinkParams = () => {
    if (params.has(PARAM_TASK_ID) || params.has(PARAM_ACTION)) setParams({}, {replace: true});
  };

  const onSkip = (taskId: number) => {
    setSkipped((prev) => skipTask(pending, prev, taskId));
    setPinnedId(undefined);
    clearLinkParams();
  };

  const onResolved = (taskId: number) => {
    setCleared((c) => c + 1);
    setResolvedIds((prev) => [...prev, taskId]);
    setSkipped((prev) => prev.filter((id) => id !== taskId));
    setPinnedId(undefined);
    clearLinkParams();
    void refresh();
  };

  const onStarted = (taskId: number) => {
    setPinnedId(taskId);
    void refresh();
  };

  const openTask = (taskId: number) => {
    setPinnedId(Number(taskId));
    setMode('focus');
  };

  const onBack = () => {
    setPinnedId(undefined);
    clearLinkParams();
  };

  const confirmLogout = () => Modal.confirm({
    title: t('complaintMobile.header.logoutConfirm'),
    okText: t('login.logout'),
    cancelText: t('common.cancel'),
    onOk: () => logout(MOBILE_LOGIN_PATH),
  });

  const firstLoad = (unresponded.loading || inProgress.loading) && !unresponded.data && !inProgress.data;
  const loadError = unresponded.error ?? inProgress.error;

  const body = () => {
    if (firstLoad) return <div className="py-24 text-center"><Spin/></div>;
    if (loadError && !pending.length && currentId === undefined) {
      return <Result status="error" title={t('complaintTask.errors.loadFailed')}
                     extra={<Button onClick={() => refresh()}>{t('complaintMobile.header.refresh')}</Button>}/>;
    }
    if (mode === 'list') return <MobileTaskList pending={pending} nowMs={nowMs} onOpen={openTask}/>;
    if (currentId === undefined) {
      return (
        <Result
          status="success"
          title={cleared > 0 ? t('complaintMobile.done.title') : t('complaintMobile.done.empty')}
          subTitle={cleared > 0 ? t('complaintMobile.done.cleared', {count: cleared}) : undefined}
          extra={<Button onClick={() => setMode('list')}>{t('complaintMobile.done.viewList')}</Button>}
        />
      );
    }
    return (
      <MobileTaskCard
        key={currentId}
        taskId={currentId}
        inQueue={inQueue}
        autoStart={linkAction === 'start' && currentId === linkTaskId}
        onAutoStartHandled={clearLinkParams}
        onStarted={onStarted}
        onResolved={onResolved}
        onSkip={onSkip}
        onBack={onBack}
      />
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="sticky top-0 z-10 bg-white shadow-sm px-4 pt-3 pb-2"
           style={{paddingTop: 'calc(env(safe-area-inset-top) + 12px)'}}>
        <div className="flex items-center justify-between">
          <span className="text-base font-semibold">{t('complaintMobile.title')}</span>
          <div className="flex items-center gap-1">
            <Button type="text" icon={mode === 'focus' ? <UnorderedListOutlined/> : <ProfileOutlined/>}
                    onClick={() => setMode(mode === 'focus' ? 'list' : 'focus')}>
              {mode === 'focus' ? t('complaintMobile.header.list') : t('complaintMobile.header.focus')}
            </Button>
            <Button type="text" icon={<ReloadOutlined/>} aria-label={t('complaintMobile.header.refresh') ?? ''}
                    onClick={() => refresh()}/>
            <Button type="text" icon={<LogoutOutlined/>} aria-label={t('login.logout') ?? ''} onClick={confirmLogout}/>
          </div>
        </div>
        {mode === 'focus' && total > 0 && (
          <div className="mt-1">
            <div className="flex justify-between text-xs text-gray-500">
              <span>
                {inQueue && position >= 0
                  ? t('complaintMobile.progress.position', {index: cleared + position + 1, total})
                  : t('complaintMobile.progress.remaining', {count: order.length})}
              </span>
              <span>{t('complaintMobile.progress.cleared', {count: cleared})}</span>
            </div>
            <Progress percent={Math.round((cleared / total) * 100)} showInfo={false} size="small"/>
          </div>
        )}
      </div>
      {body()}
    </div>
  );
};

export default MobileWorkPage;
